import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles, MapPin } from "lucide-react";
import { KineticLines, GradientButton, GhostButton, GlowOrb } from "../primitives";

const heroBg =
  "https://images.unsplash.com/photo-1694442735990-f8584f1dc746?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const stats = [
  { value: "100%", label: "Canadian-owned" },
  { value: "24–48h", label: "Response time" },
  { value: "$500+", label: "Websites from" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-[100svh] overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 -z-20">
        <img src={heroBg} alt="" className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-transparent to-ink/60" />
      </motion.div>

      <GlowOrb className="left-[-8%] top-[15%] h-[420px] w-[420px] opacity-30" color="#FF5A36" />
      <GlowOrb className="right-[-6%] top-[35%] h-[360px] w-[360px] opacity-20" color="#FFC857" />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-center px-5 pb-20 pt-32 lg:px-8"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-white backdrop-blur-md">
            <MapPin className="h-3.5 w-3.5 text-brand-gold" />
            Trusted technology partner for Canadian businesses
          </span>
        </motion.div>

        <h1 className="mt-8 max-w-4xl font-heading text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-[4.25rem]">
          <KineticLines
            lines={["Professional Websites.", "Digital Security."]}
            className="block"
          />
          <span className="block overflow-hidden py-[0.06em]">
            <motion.span
              className="block text-gradient"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.95, delay: 0.39, ease: [0.22, 1, 0.36, 1] }}
            >
              Trusted IT Solutions.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.65 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg"
        >
          Helping Canadian businesses build, secure, and grow their online presence with
          modern websites, reliable technology solutions, and ongoing support.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.8 }}
          className="mt-9 flex flex-col gap-3 sm:flex-row"
        >
          <GradientButton to="/packages" data-testid="hero-build-website">
            <Sparkles className="h-4 w-4" /> Build My Website
          </GradientButton>
          <GhostButton to="/contact" data-testid="hero-book-consultation">
            Book a Free Consultation
          </GhostButton>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-16 grid max-w-lg grid-cols-3 gap-6 border-t border-white/10 pt-8"
        >
          {stats.map((s) => (
            <div key={s.label}>
              <div className="font-heading text-2xl font-semibold text-white sm:text-3xl">{s.value}</div>
              <div className="mt-1 text-xs text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
